import { Box, Flex } from "@chakra-ui/react"
import { useEditorMode } from "@pathwright/blocks-engine"
import { List, QuillEditor } from "@pathwright/blocks/src/shared"
import Removable from "@pathwright/blocks/src/shared/Removable"
import IconButton from "@pathwright/ui/src/components/button/IconButton"
import { Content } from "@pathwright/ui/src/components/content/Content"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import { TOOLBAR_CONFIG_TYPES } from "@pathwright/ui/src/components/quill/toolbar/QuillToolbar"
import { PRIMARY_GRAY } from "@pathwright/ui/src/components/utils/colors"
import sortBy from "lodash/sortBy"
import { useCallback, useMemo, useState } from "react"
import AutosizeInput from "react-input-autosize"
import "../sortable-styles.css"
import { createSampleCard } from "../utils"
import { TabItem } from "./TabsUI"

const actionsGray = "gray"

const TabsEdit = (props) => {
  const { data, onChange } = props

  const { mode } = useEditorMode()

  const [visibleCardIndex, setVisibleCardIndex] = useState(0)

  const handleSetSelectedIndex = useCallback(
    (index) => {
      setVisibleCardIndex(index)
    },
    [setVisibleCardIndex, visibleCardIndex]
  )

  const handleChangeHTML = useCallback(
    (nextHTML) => {
      const updatedCards = data.cards.map((card, i) => {
        if (i === visibleCardIndex) return { ...card, html: nextHTML }
        return card
      })
      onChange({ cards: updatedCards })
    },
    [data, visibleCardIndex, onChange]
  )

  const handleGenerateItem = useCallback(
    ({ order }) => createSampleCard(order),
    [createSampleCard]
  )

  const handleChange = useCallback(
    ({ items }) => onChange({ cards: items }),
    [onChange]
  )

  const renderItem = useCallback(
    ({
      item,
      isAtMin,
      DragHandle,
      isActive,
      onRemove,
      onUpdate,
      onSetActive,
      index
    }) => {
      const title = item.title
      const showControls = !isAtMin
      const onChange = onUpdate

      const handleChangeTitle = (event) => {
        const nextTitle = event.target.value
        onChange({ index, attribute: "title", value: nextTitle })
      }

      return (
        <TabItem selected={isActive} onClick={onSetActive}>
          <Removable onRemove={onRemove}>
            {({ onRequestRemove }) => (
              <Flex alignItems="align-items">
                {onChange ? (
                  <AutosizeInput
                    value={title}
                    onClick={(e) => e.stopPropagation()}
                    onChange={handleChangeTitle}
                    placeholder="Enter tab title..."
                    inputStyle={{
                      fontWeight: "bold",
                      // Specify b/c of block styles
                      color: PRIMARY_GRAY,
                      outline: "none",
                      border: "none",
                      background: "none",
                      lineHeight: "1.4"
                    }}
                  />
                ) : (
                  <Text.H5>{title}</Text.H5>
                )}
                <Flex
                  className="TabItem__actions"
                  ml="1rem"
                  alignItems="center"
                >
                  {DragHandle && (
                    <DragHandle>
                      {() => (
                        <span>
                          <Pathicon
                            icon="arrow-double-vertical-2"
                            style={{
                              color: actionsGray,
                              display: "inline-block",
                              transform: "rotateZ(90deg)",
                              marginRight: "0.5rem"
                            }}
                          />
                        </span>
                      )}
                    </DragHandle>
                  )}
                  {showControls && onRemove && (
                    <IconButton
                      icon="trash"
                      size={10}
                      style={{
                        marginRight: "-6px",
                        marginLeft: "0",
                        width: "20px",
                        height: "16px",
                        color: actionsGray
                      }}
                      className="EditModeIcon"
                      onClick={(e) => {
                        e.stopPropagation()
                        onRequestRemove()
                      }}
                    />
                  )}
                </Flex>
              </Flex>
            )}
          </Removable>
        </TabItem>
      )
    },
    [mode]
  )

  const renderAdd = useCallback(
    ({ onAdd }) => (
      <Box ml="auto" p="0 10px">
        <IconButton icon="plus" className="EditModeIcon" onClick={onAdd} />
      </Box>
    ),
    []
  )

  const currentCard = useMemo(
    () => data.cards[visibleCardIndex],
    [data.cards, visibleCardIndex]
  )
  const items = useMemo(() => sortBy(data.cards, "order"), [data.cards, sortBy])

  return (
    <Content.Container>
      <Box
        border="1px solid rgba(0, 0, 0, 0.1)"
        boxShadow="0 0 20px 0 rgba(0, 0, 0, 0.1)"
      >
        <Box
          width="100%"
          overflowX="auto"
          overflowY="hidden"
          bgColor="#f5f5f5"
          style={{
            /* https://www.designernews.co/stories/91705-headsup-new-chrome-css-bug-makes-your-text-dissapear */
            /* Solves hidden tabs bug in Chrome */
            backfaceVisibility: "hidden"
          }}
          className="swiper-no-swiping"
        >
          <List
            direction="horizontal"
            listStyles={{
              display: "flex",
              width: "100%",
              justifyContent: "space-between",
              alignItems: "center"
            }}
            itemContainerStyles={{ display: "flex" }}
            items={items}
            min={2}
            renderItem={renderItem}
            renderAdd={renderAdd}
            generateItem={handleGenerateItem}
            onChange={handleChange}
            onSetActiveIndex={handleSetSelectedIndex}
            id={props.id}
          />
        </Box>
        {currentCard && (
          <Box
            padding="20px"
            /* Specify colors b/c of block styles*/
            backgroundColor="#fff"
            sx={{
              ".ql-editor p, .ql-editor li": {
                // Ignore block styles colors
                color: "#000 !important"
              }
            }}
          >
            <QuillEditor
              toolbar={TOOLBAR_CONFIG_TYPES.BASIC}
              placeholder="Enter tab content..."
              html={currentCard.html}
              onChange={handleChangeHTML}
              controlled
            />
          </Box>
        )}
      </Box>
    </Content.Container>
  )
}

export default TabsEdit
